import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { ReportPreferenceService } from 'src/app/services/report-preference.service';

export interface ReportColumn {
  /** Stable identifier — usually the AG-Grid colDef.field. */
  key: string;
  /** User-facing label. */
  label: string;
  /** Whether column is visible by default. */
  defaultVisible?: boolean;
  /** If true, user cannot hide this column (e.g. Sl. No.). */
  pinned?: boolean;
}

/**
 * Slide-in side drawer for choosing which report columns are visible / exported.
 *
 * Has a search input that filters the column list as the user types.
 * Persists per-user choice to localStorage under `report-columns:{reportSlug}`.
 * Emits the set of visible keys whenever the user toggles a column.
 */
@Component({
  selector: 'app-column-picker',
  templateUrl: './column-picker.component.html',
  styleUrls: ['./column-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ColumnPickerComponent implements OnInit, OnChanges {
  @Input() reportSlug = '';
  @Input() columns: ReportColumn[] = [];
  @Input() label = 'Columns';

  /** Visible-key set emitted on every toggle and on init (after restore). */
  @Output() visibilityChange = new EventEmitter<Set<string>>();

  open = false;
  visible = new Set<string>();
  search = '';

  constructor(private cdr: ChangeDetectorRef, private prefSvc: ReportPreferenceService) {}

  ngOnInit(): void {
    this.restore();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns'] || changes['reportSlug']) this.restore();
  }

  get filteredColumns(): ReportColumn[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.columns;
    return this.columns.filter(c =>
      c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q)
    );
  }

  private get storageKey(): string {
    // User-scoped key: report-columns:{userId}:{reportSlug}
    // Falls back to 'anon' when the user is not logged in (e.g. during tests).
    const userId = this.prefSvc.getCurrentUserId() ?? 'anon';
    return this.prefSvc.columnKey(userId, this.reportSlug || 'default');
  }

  private restore(): void {
    const next = new Set<string>();
    let saved: Set<string> | null = null;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw) saved = new Set<string>(JSON.parse(raw));
    } catch { saved = null; }

    for (const c of this.columns) {
      if (c.pinned) { next.add(c.key); continue; }
      if (saved) next.add(saved.has(c.key) ? c.key : '');
      else if (c.defaultVisible !== false) next.add(c.key);
    }
    next.delete('');
    this.visible = next;
    this.emit();
  }

  toggle(col: ReportColumn): void {
    if (col.pinned) return;
    if (this.visible.has(col.key)) this.visible.delete(col.key);
    else this.visible.add(col.key);
    this.persist();
    this.emit();
  }

  resetDefaults(): void {
    const next = new Set<string>();
    for (const c of this.columns) {
      if (c.pinned || c.defaultVisible !== false) next.add(c.key);
    }
    this.visible = next;
    this.persist();
    this.emit();
  }

  selectAll(): void {
    this.visible = new Set(this.columns.map(c => c.key));
    this.persist();
    this.emit();
  }

  clearAll(): void {
    const next = new Set<string>();
    for (const c of this.columns) if (c.pinned) next.add(c.key);
    this.visible = next;
    this.persist();
    this.emit();
  }

  isChecked(col: ReportColumn): boolean {
    return this.visible.has(col.key);
  }

  toggleOpen(event?: Event): void {
    event?.stopPropagation();
    this.open = !this.open;
    if (!this.open) this.search = '';
  }

  close(): void {
    this.open = false;
    this.search = '';
    this.cdr.markForCheck();
  }

  onSearchChange(value: string): void {
    this.search = value ?? '';
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) this.close();
  }

  private persist(): void {
    try { localStorage.setItem(this.storageKey, JSON.stringify(Array.from(this.visible))); } catch { /* quota */ }
  }

  private emit(): void {
    this.visibilityChange.emit(new Set(this.visible));
    this.cdr.markForCheck();
  }
}
